import { useQueryClient } from '@tanstack/react-query';
import { useContext, useEffect } from 'react';
import { Observable } from 'zen-observable-ts';
import { useTranslation } from 'react-i18next';
import AppContext from '../AppContext';
import { OrgWideEventPayload, OrgWideEventType } from './generated/types';
import { handleOrgWideLicenseSystemChangedEvent } from './handleOrgWideLicenseSystemChangedEvent';

const onOrgWideEvent = `subscription OnOrgWideEvent($orgId: ID!) {
    onOrgWideEvent(orgId: $orgId) {
        orgId
        eventType
        licenseSystemChanged {
            changeType
            licenseSystemId
            messageId
            sourceLicenseSystemId
            status
            targetLicenseSystemId
            errorMessage
            errorType
        }
    }
}`;

interface OrgWideEventSubscription {
    data: {
        onOrgWideEvent: OrgWideEventPayload | null;
    };
}

interface UseOrgWideEventSubscriptionInput {
    orgId: string | undefined;
    enabled?: boolean;
}

export const useOrgWideEventSubscription = ({
    orgId,
    enabled = true,
}: UseOrgWideEventSubscriptionInput) => {
    const context = useContext(AppContext);
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    useEffect(() => {
        if (!enabled || !orgId) return;

        const graphqlObserver = context.api.graphql({
            query: onOrgWideEvent,
            variables: { orgId },
        }) as unknown as Observable<OrgWideEventSubscription>;

        const subscription = graphqlObserver.subscribe({
            next: (payload) => {
                const event = payload.data.onOrgWideEvent;

                if (!event || event.orgId !== orgId) return;

                switch (event.eventType) {
                    case OrgWideEventType.LicenseSystemChanged:
                        if (!event.licenseSystemChanged) return;

                        handleOrgWideLicenseSystemChangedEvent({
                            event: event.licenseSystemChanged,
                            orgId,
                            queryClient,
                            t,
                        });
                        break;
                }
            },
            error: (error) => {
                console.error('Org-wide subscription error:', error);
                subscription.unsubscribe();
            },
        });

        return () => {
            subscription.unsubscribe();
        };
    }, [context.api, enabled, orgId, queryClient, t]);
};
